import { useMutation } from "urql";
import { graphql } from "../../../../__generated__/gql";
import ConfirmationModal from "../../../../ui/Modal/ConfirmationModal";
import Modal from "../../../../ui/Modal/Modal";
import { Controller, useForm } from "react-hook-form";
import Textarea from "../../../../ui/Textarea/Textarea";
import Label from "../../../../ui/Label/Label";
import styles from "./RegenerateArticleSectionModal.module.scss";
import Switch from "../../../../ui/Switch/Switch";
import { useState } from "react";

const RegenerateArticleSectionMutation = graphql(/* GraphQL */ `
  mutation regenerateArticleSection($input: GenerateArticleSectionInput!) {
    generateArticleSection(input: $input) {
      id
      status
    }
  }
`);

const SetArticleSectionAdditionalPromptMutation = graphql(/* GraphQL */ `
  mutation setArticleSectionAdditionalPrompt(
    $id: String!
    $additionalPrompt: String!
  ) {
    updateArticleSection(
      input: { id: $id, additionalPrompt: $additionalPrompt }
    ) {
      id
      additionalPrompt
    }
  }
`);

function InternalRegenerateArticleSectionModal({
  id,
  articleId,
  additionalPrompt,
  onClose,
}: {
  id: string;
  articleId: string;
  additionalPrompt?: string | null;
  onClose: () => void;
}) {
  const [addFeedback, setAddFeedback] = useState(!!additionalPrompt);
  const [, regenerateArticleSection] = useMutation(
    RegenerateArticleSectionMutation,
  );
  const [, setArticleSectionAdditionalPrompt] = useMutation(
    SetArticleSectionAdditionalPromptMutation,
  );
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isSubmitSuccessful },
  } = useForm<{ additionalPrompt: string }>({
    defaultValues: { additionalPrompt: additionalPrompt || "" },
  });

  const onSubmit = async (data: { additionalPrompt: string }) => {
    if (addFeedback && data.additionalPrompt !== additionalPrompt) {
      await setArticleSectionAdditionalPrompt({
        id,
        additionalPrompt: data.additionalPrompt,
      });
    }

    if (!addFeedback) {
      await setArticleSectionAdditionalPrompt({
        id,
        additionalPrompt: "",
      });
    }

    await regenerateArticleSection({
      input: {
        id,
        articleId,
      },
    });

    onClose();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <label htmlFor="add-feedback" className={styles.switch}>
        <Switch
          id="add-feedback"
          checked={addFeedback}
          onCheckedChange={() =>
            setAddFeedback((previousValue) => !previousValue)
          }
        />
        <span className={styles.switchLabel}>Add feedback</span>
      </label>
      {addFeedback && (
        <Label label="Your feedback" className={styles.feedback}>
          <Controller
            control={control}
            name="additionalPrompt"
            render={({ field }) => (
              <Textarea
                {...field}
                autosize
                minRows={3}
                placeholder="Add specific details or context"
              />
            )}
          />
        </Label>
      )}
      <ConfirmationModal
        loading={isSubmitting || isSubmitSuccessful}
        confirmationText="Regenerate"
        onClose={onClose}
      />
    </form>
  );
}

function RegenerateArticleSectionModal({
  id,
  articleId,
  additionalPrompt,
  isOpen,
  onClose,
}: {
  id: string;
  articleId: string;
  additionalPrompt?: string | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Confirm regeneration of section?"
    >
      <InternalRegenerateArticleSectionModal
        id={id}
        additionalPrompt={additionalPrompt}
        articleId={articleId}
        onClose={onClose}
      />
    </Modal>
  );
}

export default RegenerateArticleSectionModal;
